import type { PlaylistRowHonesty } from "../../repositories/playlists";

// How the playlist tables set stale placements apart: listed under a divider at the bottom
// of the table, greyed, with the last position SoundCharts reported and when.
//
// Stale rows are still listed because many of those songs are still on the playlist;
// SoundCharts just has not re-confirmed them. So they are muted rather than struck out or
// coloured as an error, and they are not counted in the header.
//
// The backend already sorts stale rows after the fresh ones, so on a paged table the
// divider sits above the first stale row of whichever page they start on.

type StaleRow = PlaylistRowHonesty & { position?: number | null };

export const DEFAULT_STALE_AFTER_DAYS = 30;

export const staleGroupLabel = (count: number) =>
  `Not confirmed recently (${count}) · listed, not counted.`;

export const staleHint = (staleAfterDays: number = DEFAULT_STALE_AFTER_DAYS) =>
  `SoundCharts hasn't re-confirmed this placement in over ${staleAfterDays} days.`;

/** Text classes for a stale row's cells, in place of the table's usual black. */
export const staleTextClass = "text-neutral-400";

/** Applied to a stale row's playlist artwork. */
export const staleArtworkClass = "opacity-50";

/** Sorts stale rows after fresh ones, leaving each group's own order alone. */
export const staleLast = (a: StaleRow, b: StaleRow) => Number(Boolean(a.isStale)) - Number(Boolean(b.isStale));

/** Whether the divider goes above this row: the first stale row on the page. */
export const startsStaleGroup = (rows: StaleRow[], index: number) =>
  Boolean(rows[index]?.isStale) && !rows[index - 1]?.isStale;

/**
 * "#12 · last confirmed 2/6/26", for the CSV exports. A stale row's position is however
 * old its confirmation is, so it never goes out bare, where it would read as current.
 */
export const stalePositionText = (item: StaleRow, formatDate: (value: string) => string) => {
  const confirmed = item.lastConfirmedAt
    ? `last confirmed ${formatDate(item.lastConfirmedAt)}`
    : "not confirmed recently";
  return item.position != null ? `#${item.position} · ${confirmed}` : confirmed;
};

interface StaleDividerRowProps {
  colSpan: number;
  /** Stale placements in the whole set, not just on this page. */
  count: number;
}

/** The thin divider row above the stale group. */
export const StaleDividerRow = ({ colSpan, count }: StaleDividerRowProps) => (
  <tr>
    <td
      colSpan={colSpan}
      className="px-4 py-1.5 text-xs font-medium border-y border-neutral-200 text-neutral-500 bg-neutral-50"
    >
      {staleGroupLabel(count)}
    </td>
  </tr>
);
